/**
 * BrandingPreferencesTab — org_admin+ page covering:
 *  - Logo + favicon upload (Tigris)
 *  - Primary / secondary color pickers with live preview chip
 *  - Welcome message
 *  - Email branding (from-name, reply-to, footer)
 *
 * Per-user preferences (avatar, timezone, etc.) live on /preferences.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, Palette, Image as ImageIcon, Mail } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { uploadImage } from '../../utils/uploadImage';

const PRESET_COLORS = [
  '#1A3C6E', '#2563EB', '#0EA5E9', '#0D9488', '#059669',
  '#65A30D', '#D97706', '#DC2626', '#DB2777', '#7C3AED', '#475569', '#0F172A',
];

function ColorField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border cursor-pointer"
          data-testid={`${testId}-input`}
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={9}
          className="font-mono w-32"
          data-testid={`${testId}-hex`}
        />
        <div className="flex flex-wrap gap-1">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={`w-5 h-5 rounded-full border-2 ${value?.toLowerCase() === c.toLowerCase() ? 'border-foreground' : 'border-transparent'}`}
              style={{ backgroundColor: c }}
              aria-label={c}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ImageUploadField({ label, value, onChange, category, testId, hint }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (file) => {
    setBusy(true);
    try {
      const res = await uploadImage(file, category);
      onChange(res.url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={value} alt={label} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handle(e.target.files[0])}
              data-testid={`${testId}-file-input`}
            />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testId}-upload-btn`}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {value ? 'Replace' : 'Upload'}
            </Button>
            {value && (
              <Button size="sm" variant="ghost" onClick={() => onChange(null)} data-testid={`${testId}-clear-btn`}>
                Remove
              </Button>
            )}
          </div>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export default function BrandingPreferencesTab() {
  const { get, put } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    logo_url: null, favicon_url: null,
    primary_color: '#1A3C6E', secondary_color: '#2E75B6',
    welcome_message: '',
    email_from_name: '', email_reply_to: '', email_footer: '',
  });

  useEffect(() => {
    get('/organizations/current/branding')
      .then(b => setForm(prev => ({ ...prev, ...b })))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load branding'))
      .finally(() => setLoading(false));
  }, [get]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await put('/organizations/current/branding', form);
      toast.success('Branding saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-lg border p-10 text-center text-muted-foreground">Loading branding…</div>;
  }

  return (
    <div className="space-y-6 pb-12" data-testid="branding-preferences-tab">
      {/* Live preview chip */}
      <div className="rounded-xl border p-4 flex items-center gap-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Preview</div>
        <div className="flex items-center gap-3 px-4 py-2 rounded-md" style={{ backgroundColor: form.primary_color, color: '#fff' }}>
          {form.logo_url
            ? <img src={form.logo_url} alt="logo" className="h-6 w-6 rounded bg-white/10 object-contain" />
            : <div className="w-6 h-6 rounded bg-white/10" />}
          <span className="font-semibold">Lumyn</span>
        </div>
        <button className="px-3 py-1.5 rounded-md text-sm text-white" style={{ backgroundColor: form.secondary_color }}>
          Sample button
        </button>
      </div>

      {/* Org branding */}
      <section className="rounded-lg border p-5 space-y-5">
        <h3 className="font-semibold flex items-center gap-2"><Palette className="w-4 h-4" />Org branding</h3>
        <ImageUploadField label="Logo" value={form.logo_url} onChange={(v) => update('logo_url', v)} category="logo" testId="logo" hint="PNG/SVG/WebP, max 5 MB. Used in topbar and emails." />
        <ImageUploadField label="Favicon" value={form.favicon_url} onChange={(v) => update('favicon_url', v)} category="favicon" testId="favicon" hint="32×32 or larger square. PNG/ICO/SVG." />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColorField label="Primary color" value={form.primary_color} onChange={(v) => update('primary_color', v)} testId="primary-color" />
          <ColorField label="Secondary color" value={form.secondary_color} onChange={(v) => update('secondary_color', v)} testId="secondary-color" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="welcome">Welcome message</Label>
          <Textarea
            id="welcome"
            value={form.welcome_message || ''}
            onChange={(e) => update('welcome_message', e.target.value)}
            maxLength={280}
            rows={2}
            placeholder="Welcome to your learning portal"
            data-testid="welcome-message-input"
          />
          <div className="text-xs text-muted-foreground text-right">{(form.welcome_message || '').length}/280</div>
        </div>
      </section>

      {/* Email branding */}
      <section className="rounded-lg border p-5 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4" />Email branding</h3>
        <p className="text-sm text-muted-foreground">Used for OTP, certificate, and notification emails sent through Resend.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="from-name">From name</Label>
            <Input
              id="from-name"
              value={form.email_from_name || ''}
              onChange={(e) => update('email_from_name', e.target.value)}
              placeholder="e.g. Acme Corp Learning"
              maxLength={80}
              data-testid="email-from-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reply-to">Reply-to address</Label>
            <Input
              id="reply-to"
              type="email"
              value={form.email_reply_to || ''}
              onChange={(e) => update('email_reply_to', e.target.value)}
              placeholder="learning@acme.com"
              maxLength={120}
              data-testid="email-reply-to"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="footer">Email footer</Label>
          <Textarea
            id="footer"
            value={form.email_footer || ''}
            onChange={(e) => update('email_footer', e.target.value)}
            placeholder="Sent by Acme Learning · Reply to this email if you have questions."
            maxLength={400}
            rows={3}
            data-testid="email-footer-input"
          />
          <div className="text-xs text-muted-foreground text-right">{(form.email_footer || '').length}/400</div>
        </div>
      </section>

      <div className="flex justify-end sticky bottom-4 z-10">
        <Button size="lg" onClick={save} disabled={saving} data-testid="branding-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
